import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/language";
import { useToast } from "@/hooks/use-toast";
import { Inbox, CheckCheck, Eye } from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

async function authedFetch(path: string, opts: RequestInit = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers: { ...(opts.headers ?? {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

type Contact = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  ip: string | null;
  seen_at: string | null;
  created_at: string;
};

export default function AdminPublicContactsPage() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const qc = useQueryClient();
  const tpc = t.adminPublicContacts;

  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-public-contacts"],
    queryFn: async () => {
      const r = await authedFetch("/api/admin/public-contacts") as { contacts?: Contact[] } | Contact[];
      if (Array.isArray(r)) return r;
      const list = r?.contacts;
      return Array.isArray(list) ? list : [];
    },
    refetchInterval: 30_000,
  });

  const markSeen = useMutation({
    mutationFn: (id: number) =>
      authedFetch(`/api/admin/public-contacts/${id}/mark-seen`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-public-contacts"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    },
  });

  const markAllSeen = useMutation({
    mutationFn: () =>
      authedFetch("/api/admin/public-contacts/mark-all-seen", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-public-contacts"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
      toast({ title: tpc.markAllDone });
    },
  });

  const contacts: Contact[] = Array.isArray(data) ? data : [];
  const unseenCount = contacts.filter(c => !c.seen_at).length;

  function toggleExpand(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function fmt(dateStr: string) {
    return new Date(dateStr).toLocaleString(t.lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Inbox className="w-6 h-6 text-blue-400" />
              <h1 className="text-2xl font-bold font-mono tracking-wide text-white">
                {tpc.title}
              </h1>
              {unseenCount > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unseenCount}
                </span>
              )}
            </div>
            <p className="text-sm text-white/50">{tpc.subtitle}</p>
          </div>
          {unseenCount > 0 && (
            <button
              onClick={() => markAllSeen.mutate()}
              disabled={markAllSeen.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              <CheckCheck className="w-4 h-4" />
              {tpc.markAllSeen}
            </button>
          )}
        </div>

        {/* State: loading / error / empty */}
        {isLoading && (
          <div className="text-white/40 text-center py-20">{t.common.loading}</div>
        )}
        {isError && (
          <div className="text-red-400 text-center py-20">{tpc.loadingError}</div>
        )}
        {!isLoading && !isError && contacts.length === 0 && (
          <div className="text-white/40 text-center py-20 flex flex-col items-center gap-3">
            <Inbox className="w-10 h-10 opacity-30" />
            <span>{tpc.noContacts}</span>
          </div>
        )}

        {/* Table */}
        {contacts.length > 0 && (
          <div className="space-y-3">
            {contacts.map(c => {
              const isNew = !c.seen_at;
              const isExpanded = expanded.has(c.id);
              const msgPreview = c.message.length > 120 ? c.message.slice(0, 120) + "…" : c.message;

              return (
                <div
                  key={c.id}
                  className={`rounded-xl border transition-colors ${
                    isNew
                      ? "border-blue-500/40 bg-blue-900/10"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                >
                  <div className="p-4 md:p-5">
                    {/* Row top: status badge + date + mark seen */}
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          isNew
                            ? "bg-blue-500 text-white"
                            : "bg-white/10 text-white/40"
                        }`}>
                          {isNew ? tpc.statusNew : tpc.statusSeen}
                        </span>
                        <span className="text-xs text-white/40">{fmt(c.created_at)}</span>
                        {c.ip && (
                          <span className="text-xs text-white/25 font-mono">IP: {c.ip}</span>
                        )}
                      </div>
                      {isNew && (
                        <button
                          onClick={() => markSeen.mutate(c.id)}
                          disabled={markSeen.isPending}
                          className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {tpc.markSeen}
                        </button>
                      )}
                    </div>

                    {/* Name / email / phone */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 mb-3">
                      <div>
                        <span className="text-xs text-white/40 uppercase tracking-wide">{tpc.colName}</span>
                        <p className="text-sm font-semibold text-white">{c.name}</p>
                        <a
                          href={`mailto:${c.email}`}
                          className="text-xs text-blue-400 hover:underline"
                        >
                          {c.email}
                        </a>
                      </div>
                      {c.phone && (
                        <div>
                          <span className="text-xs text-white/40 uppercase tracking-wide">{tpc.colPhone}</span>
                          <p className="text-sm text-white">{c.phone}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-white/40 uppercase tracking-wide">{tpc.colSubject}</span>
                        <p className="text-sm font-medium text-white">{c.subject}</p>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <span className="text-xs text-white/40 uppercase tracking-wide">{tpc.colMessage}</span>
                      <p className="text-sm text-white/80 mt-1 leading-relaxed whitespace-pre-line">
                        {isExpanded ? c.message : msgPreview}
                      </p>
                      {c.message.length > 120 && (
                        <button
                          onClick={() => toggleExpand(c.id)}
                          className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                        >
                          {isExpanded ? tpc.collapseMsg : tpc.expandMsg}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
